/* eslint-disable aveny/no-color-rule */
import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { EDDialog } from '../Dialog/EDDialog';
import { EDTypography } from '../Typography/EDTypography';
import { RapportTextEdit } from './RapportText';
import { RapportTextBoxEdit } from './RapportTextBox';
import { RapportEditBoxEdit } from './RapportEditBox';
import { RapportCheckBoxEdit } from './RapportCheckBox';
import { RapportBoxEdit } from './RapportBox';
import { RapportImageEdit } from './RapportImage';
import { RapportLineEdit } from './RapportLine';
import { RapportContainerEdit } from './RapportContainer';
import { RapportDataSetEdit } from './RapportDataSet';
import { RapportAssociationEdit } from './RapportAssociation';
import { RapportSpacingEdit } from './RapportSpacing';
import { RapportNewPageEdit } from './RapportNewPage';
import { RapportAddressWindowEdit } from './RapportAddressWindow';
import {
  IRapportMallControl,
  isRapportTextBoxControl,
  isRapportEditBoxControl,
  isRapportCheckBoxControl,
  isRapportBoxControl,
  isRapportImageControl,
  isRapportLineControl,
  isRapportContainerControl,
  isRapportDataSetControl,
  isRapportAssociationControl,
  isRapportSpacingControl,
  isRapportNewPageControl,
  isRapportAddressWindowControl,
  isRapportTextControl,
  isRapportContentControl,
  isRapportConditionControl,
  mallControlInfo,
  isRapportCustomGridControl,
} from './RapportMallTypes';
import { IRapportMallJson, UpdateControlFunction } from './RapportMall';
import { getControlByPath } from './RapportMallUtils';
import { RapportMallAddControlDialog } from './RapportMallAddControlDialog';
import { RapportMallTreeView } from './RapportMallTreeView/RapportMallTreeView';
import { RapportConditionEdit } from './RapportCondition';
import { RapportCustomGridEdit } from './RapportCustomGrid';
import EDTab from '../Tabs/Tab/EDTab';
import EDTabs from '../Tabs/EDTabs';
import { useTreeViewApiRef } from '@mui/x-tree-view-pro';

interface IRapportEgenskaperProps {
  selectedPath: number[] | null;
  onSelectPath?: (path: number[] | null) => void;
  updateControl: UpdateControlFunction;
  addControl: (controlPath: number[] | null, controlType: string) => void;
  deleteControl: (controlPath: number[]) => void;
  mall: string | undefined;
  setMall?: (mall: string) => void;
  parsedMall: IRapportMallJson | null;
  showDottedBorders?: boolean;
  setShowDottedBorders?: (v: boolean) => void;
}

export enum ViewMode {
  Egenskaper = 0,
  Tree = 1,
  Json = 2,
  Mall = 3,
}

export const RapportEgenskaper: React.FC<IRapportEgenskaperProps> = ({
  selectedPath,
  onSelectPath,
  updateControl,
  addControl,
  deleteControl,
  mall,
  setMall,
  parsedMall,
  showDottedBorders,
  setShowDottedBorders,
}) => {
  const [viewMode, setViewMode] = React.useState<ViewMode>(ViewMode.Egenskaper);
  const [jsonEdit, setJsonEdit] = React.useState<string>('');
  const [jsonError, setJsonError] = React.useState<string | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [addControlPopupOpen, setAddControlPopupOpen] =
    React.useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmText, setConfirmText] = React.useState('');
  // Advanced tabs visibility (JSON & Mall)
  const [showAdvancedTabs, setShowAdvancedTabs] = React.useState(false);
  // Whether menu was opened with ctrl-click to show advanced options
  const [menuAdvancedMode, setMenuAdvancedMode] = React.useState(false);

  // Ref for tree view scroll container
  const scrollTreeContainerRef = React.useRef<HTMLDivElement | null>(null);
  const treeApiRef = useTreeViewApiRef();

  // Wrapper för att använda getControlByPath med parsedMall
  const selectedControl: IRapportMallControl | null = useMemo(
    () => getControlByPath(parsedMall, selectedPath),
    [selectedPath, parsedMall]
  );

  // Uppdatera JSON-editor värdet när selectedPath ändras
  React.useEffect(() => {
    if (selectedPath) {
      const selectedControl = getControlByPath(parsedMall, selectedPath);
      setJsonEdit(JSON.stringify(selectedControl, null, 2));
      setJsonError(null);
    } else {
      setJsonEdit('');
      setJsonError(null);
    }
  }, [selectedPath, parsedMall]);

  // Hantera JSON-uppdatering
  const handleJsonUpdate = () => {
    if (!selectedPath) return;
    try {
      const updatedControl = JSON.parse(jsonEdit);
      updateControl(selectedPath, updatedControl);
      setJsonError(null);
    } catch (error) {
      setJsonError(
        error instanceof Error ? error.message : 'Invalid JSON format'
      );
    }
  };

  // Validera JSON medan användaren skriver
  const handleJsonChange = (value: string) => {
    setJsonEdit(value);
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch (error) {
      setJsonError(
        error instanceof Error ? error.message : 'Invalid JSON format'
      );
    }
  };

  // Menyhantering
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAdvancedMode(event.ctrlKey); // Only show advanced toggle if ctrl-click
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuAdvancedMode(false);
  };
  const handleSelectAddControl = () => {
    setAddControlPopupOpen(true);
    setAnchorEl(null);
  };

  // Ensure viewMode stays valid if advanced tabs hidden
  React.useEffect(() => {
    if (
      !showAdvancedTabs &&
      (viewMode === ViewMode.Json || viewMode === ViewMode.Mall)
    ) {
      setViewMode(ViewMode.Egenskaper);
    }
  }, [showAdvancedTabs, viewMode]);

  // Auto-scroll selected tree item into view when Tree tab active and selection changes
  const scrollSelectedIntoView = React.useCallback(() => {
    if (viewMode !== ViewMode.Tree) return;
    if (!scrollTreeContainerRef.current) return;
    const id =
      selectedPath && selectedPath.length > 0 ? selectedPath.join('-') : null;
    if (!id) return;
    requestAnimationFrame(() => {
      const el = treeApiRef?.current?.getItemDOMElement?.(id);
      if (el) {
        const contentEl =
          (el.querySelector('.MuiTreeItem-content') as HTMLElement) || el;
        const containerRect =
          scrollTreeContainerRef.current!.getBoundingClientRect();
        const elRect = contentEl.getBoundingClientRect();
        const fullyVisible =
          elRect.top >= containerRect.top &&
          elRect.bottom <= containerRect.bottom;
        if (!fullyVisible) {
          contentEl.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }
      }
    });
  }, [selectedPath, viewMode, treeApiRef]);

  // Scroll when selection changes and also on transition end
  React.useEffect(() => {
    scrollSelectedIntoView();
  }, [scrollSelectedIntoView]);

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          backgroundColor: theme => theme.customProperties.VyValjareColors.tabs,
        }}
      >
        <EDTabs value={viewMode} onChange={setViewMode} indicatorColor="white">
          <EDTab label="Egenskaper" value={ViewMode.Egenskaper} />
          <EDTab label="Trädstruktur" value={ViewMode.Tree} />
          {showAdvancedTabs && <EDTab label="JSON" value={ViewMode.Json} />}
          {showAdvancedTabs && <EDTab label="Mall" value={ViewMode.Mall} />}
        </EDTabs>
        <IconButton
          aria-label="visa meny"
          onClick={handleMenuClick}
          sx={{
            position: 'absolute',
            top: 3,
            right: 2,
            zIndex: 1,
            color: 'white',
          }}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {(menuAdvancedMode || showAdvancedTabs) && (
          <MenuItem
            onClick={() => setShowAdvancedTabs(v => !v)}
            aria-checked={showAdvancedTabs}
            role="menuitemcheckbox"
          >
            {showAdvancedTabs
              ? 'Dölj avancerade flikar'
              : 'Visa avancerade flikar'}
          </MenuItem>
        )}
        <MenuItem
          onClick={() =>
            setShowDottedBorders &&
            setShowDottedBorders(!(showDottedBorders ?? false))
          }
          aria-checked={showDottedBorders ?? false}
          role="menuitemcheckbox"
        >
          {showDottedBorders ?? false ? 'Dölj konturer' : 'Visa konturer'}
        </MenuItem>
        <MenuItem onClick={() => handleSelectAddControl()}>
          Lägg till komponent...
        </MenuItem>
        <MenuItem
          onClick={() => {
            const ctrl = selectedControl;
            if (ctrl) {
              // Bygg bekräftelsetext
              const typeName =
                mallControlInfo(ctrl)?.title?.toLowerCase() ?? 'komponenten';
              let childCount = 0;
              if (ctrl) {
                if (isRapportContentControl(ctrl))
                  childCount = ctrl.Content.length;
                else if (isRapportAssociationControl(ctrl))
                  childCount = ctrl.Template.length;
              }
              const hasChildren = childCount > 0;
              const text = hasChildren
                ? `Ta bort ${typeName} inklusive ${childCount} underkomponent${
                    childCount === 1 ? '' : 'er'
                  }?`
                : `Ta bort ${typeName}?`;
              setConfirmText(text);
              setConfirmOpen(true);
            }
            handleMenuClose();
          }}
          disabled={!selectedControl}
        >
          {'Ta bort ' +
            (mallControlInfo(selectedControl)?.title?.toLowerCase() ?? '...')}
        </MenuItem>
      </Menu>
      <RapportMallAddControlDialog
        open={addControlPopupOpen}
        onClose={controlType => {
          setAddControlPopupOpen(false);
          if (controlType !== '') {
            addControl(selectedPath, controlType);
          }
        }}
      />

      {/* Innehåll beroende på menyval – pattern 1: behåll separata scroll-containrar */}
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Egenskaper */}
        <Box
          sx={{
            px: 2,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: viewMode === ViewMode.Egenskaper ? 'flex' : 'none',
            flexDirection: 'column',
          }}
        >
          {selectedPath ? (
            <>
              {selectedControl && isRapportTextControl(selectedControl) && (
                <RapportTextEdit
                  value={selectedControl}
                  onChange={updatedControl =>
                    updateControl(selectedPath, updatedControl)
                  }
                />
              )}
              {selectedControl && isRapportTextBoxControl(selectedControl) && (
                <RapportTextBoxEdit
                  value={selectedControl}
                  onChange={updatedControl =>
                    updateControl(selectedPath, updatedControl)
                  }
                />
              )}
              {selectedControl && isRapportEditBoxControl(selectedControl) && (
                <RapportEditBoxEdit
                  value={selectedControl}
                  onChange={updatedControl =>
                    updateControl(selectedPath, updatedControl)
                  }
                />
              )}
              {selectedControl && isRapportCheckBoxControl(selectedControl) && (
                <RapportCheckBoxEdit
                  value={selectedControl}
                  onChange={updatedControl =>
                    updateControl(selectedPath, updatedControl)
                  }
                />
              )}
              {selectedControl && isRapportImageControl(selectedControl) && (
                <RapportImageEdit
                  value={selectedControl}
                  onChange={updatedControl =>
                    updateControl(selectedPath, updatedControl)
                  }
                />
              )}
              {selectedControl && isRapportBoxControl(selectedControl) && (
                <RapportBoxEdit
                  value={selectedControl}
                  onChange={updatedControl =>
                    updateControl(selectedPath, updatedControl)
                  }
                />
              )}
              {selectedControl && isRapportLineControl(selectedControl) && (
                <RapportLineEdit
                  value={selectedControl}
                  onChange={updatedControl =>
                    updateControl(selectedPath, updatedControl)
                  }
                />
              )}
              {selectedControl &&
                isRapportContainerControl(selectedControl) && (
                  <RapportContainerEdit
                    value={selectedControl}
                    onChange={updatedControl =>
                      updateControl(selectedPath, updatedControl)
                    }
                  />
                )}
              {selectedControl &&
                isRapportConditionControl(selectedControl) && (
                  <RapportConditionEdit
                    value={selectedControl}
                    onChange={updatedControl =>
                      updateControl(selectedPath, updatedControl)
                    }
                  />
                )}
              {selectedControl && isRapportDataSetControl(selectedControl) && (
                <RapportDataSetEdit
                  value={selectedControl}
                  onChange={updatedControl =>
                    updateControl(selectedPath, updatedControl)
                  }
                />
              )}
              {selectedControl &&
                  isRapportCustomGridControl(selectedControl) && (
                    <RapportCustomGridEdit
                      value={selectedControl}
                      onChange={updatedControl => {
                        updateControl(selectedPath, updatedControl);
                      }}
                    />
                  )}
                {selectedControl &&
                isRapportAssociationControl(selectedControl) && (
                  <RapportAssociationEdit
                    value={selectedControl}
                    onChange={updatedControl =>
                      updateControl(selectedPath, updatedControl)
                    }
                  />
                )}
              {selectedControl && isRapportSpacingControl(selectedControl) && (
                <RapportSpacingEdit
                  value={selectedControl}
                  onChange={updatedControl =>
                    updateControl(selectedPath, updatedControl)
                  }
                />
              )}
              {selectedControl && isRapportNewPageControl(selectedControl) && (
                <RapportNewPageEdit
                  value={selectedControl}
                  onChange={updatedControl =>
                    updateControl(selectedPath, updatedControl)
                  }
                />
              )}
              {selectedControl &&
                isRapportAddressWindowControl(selectedControl) && (
                  <RapportAddressWindowEdit
                    value={selectedControl}
                    onChange={updatedControl =>
                      updateControl(selectedPath, updatedControl)
                    }
                  />
                )}
            </>
          ) : (
            <EDTypography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              Klicka på en komponent för att visa egenskaper.
            </EDTypography>
          )}
        </Box>
        {/* Trädstruktur */}
        <Box
          sx={{
            px: 2,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: viewMode === ViewMode.Tree ? 'flex' : 'none',
            flexDirection: 'column',
          }}
          ref={(tree: HTMLDivElement | null) => {
            scrollTreeContainerRef.current = tree;
          }}
        >
          <Box sx={{ mt: 1 }}>
            <RapportMallTreeView
              parsedMall={parsedMall}
              selectedPath={selectedPath}
              onSelectPath={onSelectPath}
              onTreeItemTransitionEnd={scrollSelectedIntoView}
              apiRef={treeApiRef}
            />
          </Box>
        </Box>
        {/* JSON */}
        {showAdvancedTabs && (
          <Box
            sx={{
              px: 2,
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              display: viewMode === ViewMode.Json ? 'flex' : 'none',
              flexDirection: 'column',
            }}
          >
            {selectedPath ? (
              <Box sx={{ mt: 1 }}>
                {jsonError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    JSON fel: {jsonError}
                  </Alert>
                )}
                <TextField
                  value={jsonEdit}
                  onChange={e => handleJsonChange(e.target.value)}
                  multiline
                  minRows={12}
                  maxRows={32}
                  fullWidth
                  variant="outlined"
                  placeholder="JSON för vald komponent..."
                  error={!!jsonError}
                  inputProps={{
                    spellCheck: false,
                    style: {
                      fontFamily: 'monospace',
                      fontSize: 13,
                      background: '#fff',
                      padding: 12,
                      borderRadius: 4,
                      border: '1px solid #eee',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      overflow: 'auto',
                    },
                  }}
                />
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleJsonUpdate}
                    disabled={!!jsonError || !jsonEdit.trim()}
                  >
                    Uppdatera kontroll
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const formattedJson = JSON.stringify(
                        selectedControl,
                        null,
                        2
                      );
                      setJsonEdit(formattedJson);
                      setJsonError(null);
                    }}
                  >
                    Återställ
                  </Button>
                </Box>
              </Box>
            ) : (
              <EDTypography
                variant="body2"
                color="textSecondary"
                sx={{ mt: 2 }}
              >
                Välj en kontroll för att redigera JSON.
              </EDTypography>
            )}
          </Box>
        )}
        {/* Mall */}
        {showAdvancedTabs && (
          <Box
            sx={{
              px: 2,
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              display: viewMode === ViewMode.Mall ? 'flex' : 'none',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ mt: 1 }}>
              <TextField
                value={mall || ''}
                onChange={e => setMall && setMall(e.target.value)}
                multiline
                minRows={12}
                maxRows={32}
                fullWidth
                variant="outlined"
                inputProps={{
                  spellCheck: false,
                  style: {
                    fontFamily: 'monospace',
                    fontSize: 13,
                    background: '#fff',
                    padding: 12,
                    borderRadius: 4,
                    border: '1px solid #eee',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    overflow: 'auto',
                  },
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
      <EDDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Bekräfta borttagning"
        footerActions={[
          {
            label: 'Avbryt',
            onClick: () => setConfirmOpen(false),
            buttonProps: { flat: true },
          },
          {
            label: 'Ta bort',
            onClick: () => {
              if (selectedPath) {
                deleteControl(selectedPath);
              }
              setConfirmOpen(false);
            },
            buttonProps: { primary: true, outlined: true },
          },
        ]}
      >
        <EDTypography variant="body1">{confirmText}</EDTypography>
      </EDDialog>
    </Box>
  );
};
